import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from 'app/core/services/toast.service';
import { CurrencyUtil } from 'app/core/utils/currency.utils';
import { DefaultResponse } from 'app/shared/types';
import { finalize } from 'rxjs/operators';
import { PhysicianProcedure } from '../../../../core/dtos/physician.dto';
import {
  AppointmentPurchaseDetailsResponseDTO,
  PaymentStatus,
} from '../../dtos/purchase-details.dto';
import { CouponApiService } from '../../services/coupon-api.service';
import { CouponFormsService } from '../../services/coupon-forms.service';
import { AppointmentStatus } from '../../enums/appointment-status.enum';
import { AppointmentUpdateRequestDTO } from '../../dtos/appointment-update-request.dto';

@Component({
  selector: 'app-purchase-details',
  templateUrl: './purchase-details.component.html',
  styleUrls: ['./purchase-details.component.scss'],
})
export class PurchaseDetailsComponent implements OnInit {
  protected convertToBrazilianCurrency =
    CurrencyUtil.convertToBrazilianCurrency;

  displayModal = false;
  appointmentModalVisible = false;
  isLoading = signal(false);
  purchaseDetails = signal<AppointmentPurchaseDetailsResponseDTO | null>(null);

  private couponApiService = inject(CouponApiService);
  private couponFormsService = inject(CouponFormsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastService = inject(ToastService);

  ngOnInit(): void {
    this.loadPurchaseDetails();

    if (this.appointmentDate) {
      this.couponFormsService.formAppointment
        .get('appointmentDateTime')
        ?.setValue({ date: this.appointmentDate, time: this.appointmentDate });
    }
  }

  get formAppointment() {
    return this.couponFormsService.formAppointment;
  }

  get appointmentDateTime() {
    return this.couponFormsService.formAppointment.get('appointmentDateTime')
      ?.value;
  }

  private loadPurchaseDetails(): void {
    const appointmentId = this.route.snapshot.paramMap.get('id');

    if (!appointmentId) {
      this.toastService.showErrorToast('Erro', 'ID da consulta não encontrado');
      this.router.navigate(['/coupon/purchase-history']);
      return;
    }

    this.isLoading.set(true);

    this.couponApiService
      .getAppointmentPurchaseDetails(appointmentId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (
          response: DefaultResponse<AppointmentPurchaseDetailsResponseDTO>,
        ) => {
          if (response && !response.error && response.response) {
            this.purchaseDetails.set(response.response);
          } else {
            this.toastService.showErrorToast(
              'Erro',
              'Falha ao carregar os detalhes da compra',
            );
          }
        },
        error: () => {
          this.toastService.showErrorToast(
            'Erro',
            'Erro ao carregar detalhes da compra',
          );
          this.router.navigate(['/coupon/purchase-history']);
        },
      });
  }

  getUserCompleteAddress(): string {
    const details = this.purchaseDetails();
    if (
      !details ||
      !details.customer ||
      !details.customer.address ||
      !details.customer.address.length
    ) {
      return '';
    }

    const address = details.customer.address[0];
    return [
      address.street,
      address.number,
      address.city,
      address.state,
      address.zipCode,
    ]
      .filter(Boolean)
      .join(', ');
  }

  getPhysicianCompleteAddress(): string {
    const details = this.purchaseDetails();
    if (!details || !details.appointment) {
      return '';
    }

    return details.appointment.address;
  }

  get selectedProcedures(): PhysicianProcedure[] {
    const details = this.purchaseDetails();
    return details?.procedures || [];
  }

  get proceduresTotal(): number {
    return this.selectedProcedures.reduce(
      (acc, procedure) => acc + procedure.procedureOriginalPrice,
      0,
    );
  }

  get labelButtonCancelPurchase(): string {
    if (this.isPaid) {
      return 'Estornar compra';
    }

    if (this.coupon) {
      return 'Cancelar compra';
    }
    return 'Cancelar agendamento';
  }

  get discountTotal(): number {
    return this.selectedProcedures.reduce(
      (acc, procedure) =>
        acc +
        (procedure.procedureOriginalPrice -
          procedure.procedureFinalPriceWithTax),
      0,
    );
  }

  get proceduresPriceWithTaxTotal(): number {
    return this.selectedProcedures.reduce(
      (acc, procedure) => acc + procedure.procedureFinalPriceWithTax,
      0,
    );
  }

  get serviceTax(): number {
    return this.selectedProcedures.reduce(
      (acc, procedure) => acc + procedure.serviceTax,
      0,
    );
  }

  get discountPercent(): number {
    if (!this.proceduresTotal) return 0;
    return (this.discountTotal / this.proceduresTotal) * 100;
  }

  get finalPrice(): number {
    return this.proceduresPriceWithTaxTotal;
  }

  get customer() {
    return this.purchaseDetails()?.customer;
  }

  get physician() {
    return this.purchaseDetails()?.physician;
  }

  get coupon() {
    return this.purchaseDetails()?.coupon;
  }

  get payment() {
    return this.purchaseDetails()?.payment;
  }

  get appointment() {
    return this.purchaseDetails()?.appointment;
  }

  get couponQRCode() {
    return this.purchaseDetails()?.couponQRCodeBase64;
  }

  get paymentLink() {
    return this.purchaseDetails()?.paymentLink;
  }

  get appointmentId(): string | null {
    return this.route.snapshot.paramMap.get('id');
  }

  get isPaid(): boolean {
    return this.payment?.status === PaymentStatus.PAID;
  }

  get isCanceled() {
    return this.appointment?.status === AppointmentStatus.CANCELED;
  }

  get appointmentDate(): Date | null {
    if (!this.appointment?.datetime) return null;
    return new Date(this.appointment.datetime);
  }

  goBack() {
    this.router.navigate(['/coupon/purchase-history']);
  }

  copyLink() {
    if (this.paymentLink) {
      navigator.clipboard.writeText(this.paymentLink);
      this.toastService.showSuccessToast('', 'Link de pagamento copiado');
    }
  }

  editAppointment() {
    this.appointmentModalVisible = true;
    if (this.appointmentDate) {
      this.couponFormsService.formAppointment
        .get('appointmentDateTime')
        ?.setValue({ date: this.appointmentDate, time: this.appointmentDate });
    }
  }

  saveAppointment() {
    if (!this.appointmentId || !this.customer) {
      this.toastService.showErrorToast(
        'Erro',
        'Não foi possível identificar a consulta ou o cliente',
      );
      this.appointmentModalVisible = false;
      return;
    }

    if (this.couponFormsService.formAppointment.invalid) {
      this.toastService.showErrorToast(
        'Erro',
        'Por favor, selecione uma data e hora válidas',
      );
      return;
    }

    const appointmentValue = this.appointmentDateTime;
    let datetimeToAppointment = new Date();

    if (appointmentValue && appointmentValue.date && appointmentValue.time) {
      const year = appointmentValue.date.getFullYear();
      const month = appointmentValue.date.getMonth();
      const day = appointmentValue.date.getDate();
      const hours = appointmentValue.time.getHours();
      const minutes = appointmentValue.time.getMinutes();

      datetimeToAppointment = new Date(year, month, day, hours, minutes, 0, 0);
    }

    this.isLoading.set(true);

    const updateData: AppointmentUpdateRequestDTO = {
      customerId: this.customer.id,
      datetime: datetimeToAppointment,
    };

    this.couponApiService
      .updateAppointment(this.appointmentId, updateData)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: response => {
          if (response && !response.error) {
            this.toastService.showSuccessToast(
              'Agendamento alterado',
              'Agendamento alterado com sucesso.',
            );
            this.appointmentModalVisible = false;
            this.loadPurchaseDetails();
          } else {
            this.toastService.showErrorToast(
              'Erro',
              'Falha ao atualizar o agendamento',
            );
          }
        },
        error: error => {
          this.toastService.showErrorToast(
            'Erro',
            error.response?.message || 'Erro ao atualizar o agendamento',
          );
        },
      });
  }

  closeAppointmentModal() {
    this.appointmentModalVisible = false;
  }

  openCancelPurchaseModal() {
    this.displayModal = true;
  }

  cancelPurchase() {
    if (!this.appointmentId) {
      this.toastService.showErrorToast(
        'Erro',
        'Não foi possível identificar a consulta',
      );
      this.displayModal = false;
      return;
    }

    this.isLoading.set(true);

    this.couponApiService
      .cancelAppointment(this.appointmentId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: response => {
          if (response && !response.error) {
            this.toastService.showSuccessToast(
              'Compra cancelada',
              'Compra cancelada com sucesso.',
            );
            this.displayModal = false;
            this.loadPurchaseDetails();
          } else {
            this.toastService.showErrorToast(
              'Erro',
              'Falha ao cancelar a compra',
            );
          }
        },
        error: error => {
          this.toastService.showErrorToast(
            'Erro',
            error.response?.message || 'Erro ao cancelar a compra',
          );
        },
      });
  }

  resendLink() {
    if (!this.appointmentId) {
      this.toastService.showErrorToast(
        'Erro',
        'Não foi possível identificar a consulta',
      );
      return;
    }

    this.isLoading.set(true);

    this.couponApiService
      .sendPaymentLink(this.appointmentId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: response => {
          if (response && !response.error) {
            this.toastService.showSuccessToast(
              '',
              'Link de pagamento enviado para o cliente.',
            );
          } else {
            this.toastService.showErrorToast(
              'Erro',
              'Falha ao reenviar o link de pagamento',
            );
          }
        },
        error: error => {
          this.toastService.showErrorToast(
            'Erro',
            error.response?.message || 'Erro ao reenviar o link de pagamento',
          );
        },
      });
  }
}
